import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  endOfWeek as dateFnsEndOfWeek,
  startOfWeek as dateFnsStartOfWeek,
} from "date-fns";
import { DateTime } from "luxon";

export type WeekStartsOn = 0 | 1;

export const DEFAULT_WEEK_STARTS_ON: WeekStartsOn = 1;

const JS_WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;
const JS_WEEKDAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"] as const;

export const asWeekStartsOn = (value: unknown): WeekStartsOn => {
  if (value === 0 || value === "0") {
    return 0;
  }
  if (value === 1 || value === "1") {
    return 1;
  }
  return DEFAULT_WEEK_STARTS_ON;
};

export const readSessionWeekStartsOn = (coach: unknown): WeekStartsOn => {
  if (!coach || typeof coach !== "object") {
    return DEFAULT_WEEK_STARTS_ON;
  }
  return asWeekStartsOn((coach as { weekStartsOn?: unknown }).weekStartsOn);
};

export const startOfWeek = (date: Date, weekStartsOn: WeekStartsOn): Date =>
  dateFnsStartOfWeek(date, { weekStartsOn });

export const endOfWeek = (date: Date, weekStartsOn: WeekStartsOn): Date =>
  dateFnsEndOfWeek(date, { weekStartsOn });

export const getWeekRange = (date: Date, weekStartsOn: WeekStartsOn) => ({
  from: startOfWeek(date, weekStartsOn),
  to: endOfDay(endOfWeek(date, weekStartsOn)),
});

export const getWeekDays = (date: Date, weekStartsOn: WeekStartsOn): Date[] => {
  const weekStart = startOfWeek(date, weekStartsOn);
  return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
};

export const realignWeekDays = (
  selectedDays: Date[],
  weekStartsOn: WeekStartsOn,
): Date[] => {
  const midWeekDay = selectedDays[Math.floor(selectedDays.length / 2)];
  const anchor = midWeekDay ?? selectedDays[0] ?? new Date();
  return getWeekDays(anchor, weekStartsOn);
};

export const weekdayLabels = (weekStartsOn: WeekStartsOn): string[] => [
  ...JS_WEEKDAY_LABELS.slice(weekStartsOn),
  ...JS_WEEKDAY_LABELS.slice(0, weekStartsOn),
];

export const weekdayLetters = (weekStartsOn: WeekStartsOn): string[] => [
  ...JS_WEEKDAY_LETTERS.slice(weekStartsOn),
  ...JS_WEEKDAY_LETTERS.slice(0, weekStartsOn),
];

/** Stored fixed-schedule `day_index` is Monday=0. */
export const fixedDayIndexToColumn = (
  dayIndex: number,
  weekStartsOn: WeekStartsOn,
): number => {
  const mondayBased = ((dayIndex % 7) + 7) % 7;
  if (weekStartsOn === 1) {
    return mondayBased;
  }
  return (mondayBased + 1) % 7;
};

export const columnToFixedDayIndex = (
  column: number,
  weekStartsOn: WeekStartsOn,
): number => {
  const visualColumn = ((column % 7) + 7) % 7;
  if (weekStartsOn === 1) {
    return visualColumn;
  }
  return (visualColumn + 6) % 7;
};

export const visualWeekdayIndex = (
  date: Date,
  weekStartsOn: WeekStartsOn,
): number => (date.getDay() - weekStartsOn + 7) % 7;

export const dateFromFixedDayIndex = (
  mondayWeekStart: Date,
  dayIndex: number,
): Date => addDays(mondayWeekStart, dayIndex);

export const fixedDayIndexFromDate = (
  mondayWeekStart: Date,
  date: Date,
): number => differenceInCalendarDays(date, mondayWeekStart);

export const isFixedDayIndexInView = (
  dayIndex: number,
  visibleDays: number,
): boolean => dayIndex >= 0 && dayIndex < visibleDays;

export const fixedVisualDays = (
  mondayWeekStart: Date,
  visibleDays: number,
  weekStartsOn: WeekStartsOn,
): Date[] => {
  const dataEnd = addDays(mondayWeekStart, Math.max(visibleDays, 1) - 1);
  const visualStart = startOfWeek(mondayWeekStart, weekStartsOn);
  const days: Date[] = [];
  for (let date = visualStart; date <= dataEnd; date = addDays(date, 1)) {
    days.push(date);
  }
  return days;
};

export const luxonStartOfWeek = (
  dateTime: DateTime,
  weekStartsOn: WeekStartsOn,
): DateTime => {
  const jsWeekday = dateTime.weekday === 7 ? 0 : dateTime.weekday;
  const daysFromStart = (jsWeekday - weekStartsOn + 7) % 7;
  return dateTime.minus({ days: daysFromStart }).startOf("day");
};

export const luxonEndOfWeek = (
  dateTime: DateTime,
  weekStartsOn: WeekStartsOn,
): DateTime => luxonStartOfWeek(dateTime, weekStartsOn).plus({ days: 6 }).endOf("day");

export const luxonLeadingEmptyDays = (
  luxonWeekday: number,
  weekStartsOn: WeekStartsOn,
): number => {
  const jsWeekday = luxonWeekday === 7 ? 0 : luxonWeekday;
  return (jsWeekday - weekStartsOn + 7) % 7;
};
